/**
 * 장비 창 (E). 슬롯 클릭으로 해제, 주문서 대기 중이면 강화.
 */

import Phaser from 'phaser';
import type { AssetTable } from '../assets/loader';
import { Sfx } from '../audio/Sfx';
import { SLOT_LABEL, SLOT_ORDER, RARITY, RARITY_COLOR, type EquipSlot } from '../data/equipment';
import { ITEMS } from '../data/items';
import { EV } from '../game/events';
import type { PlayerState } from '../systems/PlayerState';
import { COLOR, TEXT_COLOR, UI_FONT, UI_MONO } from './theme';

const WIDTH = 280;
const HEIGHT = 430;
const PAD = 14;

export class EquipmentWindow {
  private readonly container: Phaser.GameObjects.Container;
  private readonly rows: Phaser.GameObjects.Container[] = [];
  private readonly hint: Phaser.GameObjects.Text;

  constructor(
    scene: Phaser.Scene,
    private readonly state: PlayerState,
    private readonly assets: AssetTable,
  ) {
    const x = scene.scale.width / 2 - WIDTH / 2 - 160;
    const y = 64;
    this.container = scene.add.container(x, y).setVisible(false).setDepth(32);

    const g = scene.add.graphics();
    g.fillStyle(COLOR.panelEdge, 0.96);
    g.fillRoundedRect(-4, -4, WIDTH + 8, HEIGHT + 8, 12);
    g.fillStyle(COLOR.window, 0.98);
    g.fillRoundedRect(0, 0, WIDTH, HEIGHT, 10);
    this.container.add(g);

    const title = scene.add.text(PAD, 10, '장비', {
      fontFamily: UI_FONT,
      fontSize: '16px',
      color: TEXT_COLOR.dark,
    });
    const close = scene.add
      .text(WIDTH - PAD, 12, '✕', {
        fontFamily: UI_MONO,
        fontSize: '14px',
        color: TEXT_COLOR.dark,
      })
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true });
    close.on('pointerdown', () => this.close());
    this.hint = scene.add.text(PAD, HEIGHT - 52, '', {
      fontFamily: UI_FONT,
      fontSize: '12px',
      color: TEXT_COLOR.darkDim,
      wordWrap: { width: WIDTH - PAD * 2 },
    });
    this.container.add([title, close, this.hint]);

    this.state.events.on(EV.gearChanged, this.refresh, this);
    this.state.events.on(EV.statsChanged, this.refresh, this);
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.state.events.off(EV.gearChanged, this.refresh, this);
      this.state.events.off(EV.statsChanged, this.refresh, this);
    });
    this.refresh();
  }

  toggle(): boolean {
    this.container.setVisible(!this.container.visible);
    if (this.container.visible) this.refresh();
    return this.container.visible;
  }

  close(): void {
    this.container.setVisible(false);
  }

  get visible(): boolean {
    return this.container.visible;
  }

  private refresh = (): void => {
    for (const row of this.rows) row.destroy();
    this.rows.length = 0;
    const scene = this.container.scene;
    SLOT_ORDER.forEach((slot, i) => {
      const y = 40 + i * 48;
      const piece = this.state.worn[slot];
      const row = scene.add.container(0, y);
      const label = scene.add.text(PAD, 0, SLOT_LABEL[slot], {
        fontFamily: UI_MONO,
        fontSize: '12px',
        color: TEXT_COLOR.darkDim,
      });
      row.add(label);
      if (piece) {
        const def = ITEMS[piece.itemId];
        const rare = RARITY[piece.rarity];
        const icon = scene.add.image(PAD + 14, 28, def?.texture ?? 'item-meso', 0);
        icon.setScale(Math.min(1, 22 / this.assets.get(def?.texture ?? 'item-meso').frameHeight));
        icon.setInteractive({ useHandCursor: true });
        icon.on('pointerdown', () => this.onSlot(slot));
        const text = scene.add
          .text(PAD + 36, 18, `${def?.name ?? piece.itemId}\n${rare?.name ?? ''}  공+${piece.watk} 방+${piece.def} 명+${piece.acc}  (${piece.upgradesLeft})`, {
            fontFamily: UI_FONT,
            fontSize: '11px',
            color: RARITY_COLOR[piece.rarity],
            wordWrap: { width: WIDTH - PAD - 48, useAdvancedWrap: true },
          })
          .setInteractive({ useHandCursor: true });
        text.on('pointerdown', () => this.onSlot(slot));
        row.add([icon, text]);
      } else {
        const empty = scene.add
          .text(PAD, 18, '비어 있음', {
            fontFamily: UI_FONT,
            fontSize: '12px',
            color: TEXT_COLOR.darkDim,
          })
          .setInteractive({ useHandCursor: true });
        empty.on('pointerdown', () => this.onSlot(slot));
        row.add(empty);
      }
      this.container.add(row);
      this.rows.push(row);
    });
    this.hint.setText(
      this.state.pendingScroll
        ? '주문서를 고른 상태입니다. 슬롯을 클릭해 강화하세요.'
        : '클릭: 해제 (무기는 교체만) · 소비 탭에서 주문서',
    );
  };

  private onSlot(slot: EquipSlot): void {
    if (this.state.pendingScroll) {
      if (this.state.applyPendingToWorn(slot)) Sfx.uiClick();
      else Sfx.deny();
      this.refresh();
      return;
    }
    if (this.state.unequip(slot)) Sfx.uiClick();
    else Sfx.deny();
  }
}
